import { diffLines, type Change } from 'diff';
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';

type DiffLine = {
  left?: string;
  right?: string;
  type: 'same' | 'added' | 'removed';
};

type TextDiffProps = {
  left: string;
  right: string;
  leftTitle?: string;
  rightTitle?: string;
  maxHeight?: number;
};

const TextDiff = ({ left, right, leftTitle = 'A', rightTitle = 'B', maxHeight = 420 }: TextDiffProps) => {
  const theme = useTheme();
  const diffLinesResult = useMemo<DiffLine[]>(() => {
    const parts: Change[] = diffLines(left ?? '', right ?? '');
    const lines: DiffLine[] = [];

    parts.forEach((part: Change) => {
      const chunkLines: string[] = part.value.split('\n');
      const normalizedLines = chunkLines[chunkLines.length - 1] === '' ? chunkLines.slice(0, -1) : chunkLines;

      normalizedLines.forEach((line: string) => {
        if (part.added) {
          lines.push({ right: line, type: 'added' });
        } else if (part.removed) {
          lines.push({ left: line, type: 'removed' });
        } else {
          lines.push({ left: line, right: line, type: 'same' });
        }
      });
    });

    return lines;
  }, [left, right]);

  const borderColor = theme.palette.divider;
  const headerBg = theme.palette.background.default;
  const surfaceBg = theme.palette.background.paper;
  const addedBg = theme.palette.success.light;
  const removedBg = theme.palette.error.light;
  const textColor = theme.palette.text.primary;

  return (
    <div className="border rounded" style={{ borderColor }}>
      <div className="grid grid-cols-2 text-xs font-semibold border-b" style={{ borderColor, background: headerBg }}>
        <div className="px-2 py-1 border-r" style={{ borderColor, color: textColor }}>
          {leftTitle}
        </div>
        <div className="px-2 py-1" style={{ color: textColor }}>
          {rightTitle}
        </div>
      </div>
      <div className="overflow-auto" style={{ maxHeight, background: surfaceBg }}>
        {diffLinesResult.map((line, index) => (
          <div key={`${index}-${line.type}`} className="grid grid-cols-2 text-xs font-mono">
            <div
              className="px-2 py-1 border-r whitespace-pre-wrap"
              style={{
                borderColor,
                background: line.type === 'removed' ? removedBg : surfaceBg,
                color: textColor,
              }}
            >
              {line.left ?? ''}
            </div>
            <div
              className="px-2 py-1 whitespace-pre-wrap"
              style={{
                background: line.type === 'added' ? addedBg : surfaceBg,
                color: textColor,
              }}
            >
              {line.right ?? ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TextDiff;
